import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createSlots as _createSlots, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "upcoming-meeting__header" }
const _hoisted_3 = {
  key: 0,
  class: "calendar-events__list"
}
const _hoisted_4 = {
  key: 2,
  class: "calendar-events__buttons"
}
const _hoisted_5 = { class: "calendar-meeting__flex-wrapper" }
const _hoisted_6 = {
  key: 0,
  class: "calendar-meeting__header"
}
const _hoisted_7 = {
  key: 1,
  class: "calendar-meeting--loading"
}
const _hoisted_8 = { key: 2 }
const _hoisted_9 = {
  key: 0,
  class: "calendar-meeting__invalid-hint"
}
const _hoisted_10 = {
  key: 1,
  class: "calendar-meeting__attendees"
}

import type { Conversation, Participant } from '../types/index.ts'

import { showSuccess } from '@nextcloud/dialogs'
import { n, t } from '@nextcloud/l10n'
import { useIsMobile } from '@nextcloud/vue/composables/useIsMobile'
import { usernameToColor } from '@nextcloud/vue/functions/usernameToColor'
import debounce from 'debounce'
import { computed, onBeforeMount, provide, ref, watch } from 'vue'
import { useStore } from 'vuex'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcCheckboxRadioSwitch from '@nextcloud/vue/components/NcCheckboxRadioSwitch'
import NcDateTimePickerNative from '@nextcloud/vue/components/NcDateTimePickerNative'
import NcDialog from '@nextcloud/vue/components/NcDialog'
import NcEmptyContent from '@nextcloud/vue/components/NcEmptyContent'
import NcLoadingIcon from '@nextcloud/vue/components/NcLoadingIcon'
import NcPopover from '@nextcloud/vue/components/NcPopover'
import NcSelect from '@nextcloud/vue/components/NcSelect'
import NcTextArea from '@nextcloud/vue/components/NcTextArea'
import NcTextField from '@nextcloud/vue/components/NcTextField'
import IconAccountPlusOutline from 'vue-material-design-icons/AccountPlusOutline.vue'
import IconAccountSearchOutline from 'vue-material-design-icons/AccountSearchOutline.vue'
import IconCalendarBlankOutline from 'vue-material-design-icons/CalendarBlankOutline.vue'
import IconCheck from 'vue-material-design-icons/Check.vue'
import IconPlus from 'vue-material-design-icons/Plus.vue'
import SelectableParticipant from './BreakoutRoomsEditor/SelectableParticipant.vue'
import CalendarEventSmall from './UIShared/CalendarEventSmall.vue'
import ContactSelectionBubble from './UIShared/ContactSelectionBubble.vue'
import SearchBox from './UIShared/SearchBox.vue'
import StaticDateTime from './UIShared/StaticDateTime.vue'
import TransitionWrapper from './UIShared/TransitionWrapper.vue'
import { ATTENDEE, CONVERSATION } from '../constants.ts'
import { hasTalkFeature } from '../services/CapabilitiesManager.ts'
import { useGroupwareStore } from '../stores/groupware.ts'
import { convertToUnix, ONE_HOUR_IN_MS } from '../utils/formattedTime.ts'
import { getDisplayNameWithFallback } from '../utils/getDisplayName.ts'

type CalendarOption = { value: string, label: string, color: string }

export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarEventsDialog',
  props: {
    token: {},
    container: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const hideTriggers = (triggers: string[]) => [...triggers, 'click']

const store = useStore()
const groupwareStore = useGroupwareStore()
const isMobile = useIsMobile()

// Add a visual bulk selection state for SelectableParticipant component
provide('bulkParticipantsSelection', true)

const isFormOpen = ref(false)
const isSelectorOpen = ref(false)
const loading = ref(Object.keys(groupwareStore.calendars).length === 0)
const submitting = ref(false)

const calendars = computed(() => groupwareStore.calendars)
const upcomingEvents = computed(() => {
	const now = convertToUnix(Date.now())
	return groupwareStore.getAllEvents(props.token)
		.sort((a, b) => (a.start && b.start) ? (a.start - b.start) : 0)
		.map((event) => {
			const start = event.start
				? (event.start <= now) ? t('spreed', 'Now') : event.start * 1000
				: ''
			const color = calendars.value[event.calendarUri]?.color ?? usernameToColor(event.calendarUri).color

			return { ...event, start, color, href: event.calendarAppUrl ?? undefined }
		})
})

const calendarOptions = computed<CalendarOption[]>(() => groupwareStore.writeableCalendars.map((calendar) => ({
	value: calendar.uri,
	label: calendar.displayname,
	color: calendar.color ?? usernameToColor(calendar.uri).color,
})))
const canScheduleMeeting = computed(() => {
	return hasTalkFeature(props.token, 'schedule-meeting') && store.getters.isModerator && calendarOptions.value.length !== 0
		&& conversation.value?.type !== CONVERSATION.TYPE.ONE_TO_ONE_FORMER
})

const selectedCalendar = ref<CalendarOption | null>(null)
const selectedDateTimeStart = ref(getCurrentDateInStartOfNthHour(1))
const selectedDateTimeEnd = ref(getCurrentDateInStartOfNthHour(2))
const newMeetingTitle = ref('')
const newMeetingDescription = ref('')
const invalid = ref<string | null>(null)
const invalidHint = computed(() => {
	switch (invalid.value) {
		case null:
			return ''
		case 'calendar':
			return t('spreed', 'Invalid calendar selected')
		case 'start':
			return t('spreed', 'Invalid start time selected')
		case 'end':
			return t('spreed', 'Invalid end time selected')
		case 'unknown':
		default:
			return t('spreed', 'Unknown error occurred')
	}
})

const selectAll = ref(true)
const selectedAttendeeIds = ref<number[]>([])
const attendeeHint = computed(() => {
	if (!selectedAttendeeIds.value?.length) {
		return t('spreed', 'Sending no invitations')
	}

	const list: Participant[] = selectedParticipants.value.slice(0, 2)
	const remainingCount = selectedParticipants.value.length - list.length
	const summary = list.map((participant) => getDisplayNameWithFallback(participant.displayName, participant.actorType))

	if (remainingCount === 0) {
		// Amount is 2 or less
		switch (summary.length) {
			case 1: {
				return t('spreed', '{participant0} will receive an invitation', { participant0: summary[0] }, undefined, {
					escape: false,
					sanitize: false,
				})
			}
			case 2: {
				return t('spreed', '{participant0} and {participant1} will receive invitations', { participant0: summary[0], participant1: summary[1] }, undefined, {
					escape: false,
					sanitize: false,
				})
			}
			case 0:
			default: {
				return ''
			}
		}
	} else {
		return n('spreed', '{participant0}, {participant1} and %n other will receive invitations', '{participant0}, {participant1} and %n others will receive invitations', remainingCount, { participant0: summary[0], participant1: summary[1] }, {
			escape: false,
			sanitize: false,
		})
	}
})

const searchText = ref('')
const isMatch = (string: string = '') => string.toLowerCase().includes(searchText.value.toLowerCase())

const conversation = computed<Conversation | undefined>(() => store.getters.conversation(props.token))
const participants = computed(() => {
	if (!conversation.value) {
		return []
	}
	if (isOneToOneConversation.value && store.getters.participantsList(props.token).length === 1) {
		// Second participant is not yet added to conversation, need to fake data from conversation object
		// We do not have an attendeeId, so 'attendeeIds' in payload should be 'null' (selectAll === true)
		return [{ id: conversation.value.name, source: ATTENDEE.ACTOR_TYPE.USERS, displayName: conversation.value.displayName }]
	}
	return store.getters.participantsList(props.token).filter((participant: Participant) => {
		return [ATTENDEE.ACTOR_TYPE.USERS, ATTENDEE.ACTOR_TYPE.EMAILS].includes(participant.actorType)
			&& participant.attendeeId !== conversation.value!.attendeeId
	})
})
const participantsInitialised = computed(() => store.getters.participantsInitialised(props.token))
const filteredParticipants = computed(() => participants.value.filter((participant: Participant) => {
	return isMatch(participant.displayName)
		|| (participant.actorType === ATTENDEE.ACTOR_TYPE.USERS && isMatch(participant.actorId))
		|| (participant.actorType === ATTENDEE.ACTOR_TYPE.EMAILS && participant.invitedActorId && isMatch(participant.invitedActorId))
}))
const selectedParticipants = computed(() => participants.value
	.filter((participant: Participant) => selectedAttendeeIds.value.includes(participant.attendeeId))
	.sort((a: Participant, b: Participant) => {
		if (a.actorType === ATTENDEE.ACTOR_TYPE.USERS && b.actorType === ATTENDEE.ACTOR_TYPE.EMAILS) {
			return -1
		} else if (a.actorType === ATTENDEE.ACTOR_TYPE.EMAILS && b.actorType === ATTENDEE.ACTOR_TYPE.USERS) {
			return 1
		} else if (a.actorType === ATTENDEE.ACTOR_TYPE.EMAILS && b.actorType === ATTENDEE.ACTOR_TYPE.EMAILS
			&& (!a.displayName || !b.displayName)) {
			return a.displayName ? -1 : 1
		}
		return 0
	}))

const isOneToOneConversation = computed(() => {
	return conversation.value?.type === CONVERSATION.TYPE.ONE_TO_ONE
		|| conversation.value?.type === CONVERSATION.TYPE.ONE_TO_ONE_FORMER
})

const inviteLabel = computed(() => {
	return isOneToOneConversation.value
		? t('spreed', 'Invite {user}', { user: conversation.value?.displayName ?? '' })
		: t('spreed', 'Invite all users and emails in this conversation')
})

const debounceAdjustCurrentTimeRange = debounce(adjustCurrentTimeRange, 500)

onBeforeMount(() => {
	getCalendars()
})

watch(isFormOpen, (value) => {
	if (!value) {
		return
	}

	// Reset the default form values
	selectedCalendar.value = calendarOptions.value.find((o) => o.value === groupwareStore.defaultCalendarUri) ?? null
	selectedDateTimeStart.value = getCurrentDateInStartOfNthHour(1)
	selectedDateTimeEnd.value = getCurrentDateInStartOfNthHour(2)
	newMeetingTitle.value = ''
	newMeetingDescription.value = ''
	selectedAttendeeIds.value = participants.value.map((participant: Participant) => participant.attendeeId)
	searchText.value = ''
	selectAll.value = true
	invalid.value = null
})

watch([selectedCalendar, selectedDateTimeStart, selectedDateTimeEnd], () => {
	invalid.value = null
})

watch(participants, (value) => {
	if (selectAll.value) {
		selectedAttendeeIds.value = value.map((participant: Participant) => participant.attendeeId)
	}
})

watch(selectedDateTimeStart, () => debounceAdjustCurrentTimeRange('end'))
watch(selectedDateTimeEnd, () => debounceAdjustCurrentTimeRange('start'))

/**
 * Autocorrect end date, if start date is after end date (or vice versa)
 *
 * @param direction counterpart to be adjusted
 */
function adjustCurrentTimeRange(direction: 'start' | 'end') {
	if (selectedDateTimeStart.value < selectedDateTimeEnd.value) {
		// All good, no adjustment needed
		return
	} else if (direction === 'end') {
		// Keep the end time 1 hour ahead
		selectedDateTimeEnd.value = new Date(selectedDateTimeStart.value.getTime() + ONE_HOUR_IN_MS)
	} else {
		// Keep the start time 1 hour behind
		selectedDateTimeStart.value = new Date(selectedDateTimeEnd.value.getTime() - ONE_HOUR_IN_MS)
	}
}

/**
 * Returns Date object with N hours from now at the start of hour
 *
 * @param hours amount of hours to add
 */
function getCurrentDateInStartOfNthHour(hours: number) {
	const date = new Date()
	date.setHours(date.getHours() + hours, 0, 0, 0)
	return date
}

/**
 * Toggle selected attendees
 *
 * @param value switch value
 */
function toggleAll(value: boolean) {
	selectedAttendeeIds.value = value ? participants.value.map((participant: Participant) => participant.attendeeId) : []
}

/**
 * Remove selected attendee from contact bubble
 *
 * @param value switch value
 */
function removeSelectedParticipant(value: Participant) {
	selectedAttendeeIds.value = selectedAttendeeIds.value.filter((id) => value.attendeeId !== id)
}

/**
 * Check selected attendees
 *
 * @param value array of ids
 */
function checkSelection(value: number[]) {
	selectAll.value = participants.value.length === value.length
}

/**
 * Get user's calendars to identify belonging of known and future events
 */
async function getCalendars() {
	await groupwareStore.getDefaultCalendarUri()
	await groupwareStore.getPersonalCalendars()
	loading.value = false
}

/**
 * Get user's calendars to identify belonging of known and future events
 */
async function submitNewMeeting() {
	if (!selectedCalendar.value) {
		invalid.value = 'calendar'
		return
	}
	if (selectedDateTimeStart.value < new Date()) {
		invalid.value = 'start'
		return
	}
	if (selectedDateTimeEnd.value < new Date() || selectedDateTimeEnd.value < selectedDateTimeStart.value) {
		invalid.value = 'end'
		return
	}

	try {
		submitting.value = true
		await groupwareStore.scheduleMeeting(props.token, {
			calendarUri: selectedCalendar.value.value,
			start: convertToUnix(selectedDateTimeStart.value),
			end: convertToUnix(selectedDateTimeEnd.value),
			title: newMeetingTitle.value || null,
			description: newMeetingDescription.value || null,
			attendeeIds: selectAll.value ? null : selectedAttendeeIds.value,
		})
		showSuccess(t('spreed', 'Meeting created'))
		isFormOpen.value = false
	} catch (error) {
		// @ts-expect-error Vue: Property response does not exist
		invalid.value = error?.response?.data?.ocs?.data?.error ?? 'unknown'
	} finally {
		submitting.value = false
	}
}

return (_ctx: any,_cache: any) => {
  return (conversation.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(NcPopover), {
          container: __props.container,
          "popper-hide-triggers": hideTriggers,
          "no-focus-trap": !canScheduleMeeting.value && upcomingEvents.value.length === 0,
          "popup-role": "dialog"
        }, {
          trigger: _withCtx(() => [
            _createVNode(_unref(NcButton), {
              class: "upcoming-meeting",
              title: _unref(t)('spreed', 'Upcoming meetings'),
              "aria-label": _unref(t)('spreed', 'Upcoming meetings')
            }, _createSlots({
              icon: _withCtx(() => [
                _createVNode(IconCalendarBlankOutline, { size: 20 })
              ]),
              _: 2
            }, [
              (upcomingEvents.value[0] && !_unref(isMobile))
                ? {
                    name: "default",
                    fn: _withCtx(() => [
                      _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(t)('spreed', 'Next meeting')), 1),
                      _createVNode(StaticDateTime, {
                        time: upcomingEvents.value[0].start,
                        calendar: ""
                      }, null, 8, ["time"])
                    ]),
                    key: "0"
                  }
                : undefined
            ]), 1032, ["title", "aria-label"])
          ]),
          default: _withCtx(() => [
            (!loading.value && upcomingEvents.value.length)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(upcomingEvents.value, (event) => {
                    return (_openBlock(), _createBlock(CalendarEventSmall, {
                      key: event.uri,
                      name: event.summary,
                      start: event.start,
                      href: event.href,
                      color: event.color,
                      "is-recurring": !!event.recurrenceId
                    }, null, 8, ["name", "start", "href", "color", "is-recurring"]))
                  }), 128))
                ]))
              : (_openBlock(), _createBlock(_unref(NcEmptyContent), {
                  key: 1,
                  class: "calendar-events__empty-content"
                }, {
                  icon: _withCtx(() => [
                    (loading.value)
                      ? (_openBlock(), _createBlock(_unref(NcLoadingIcon), { key: 0 }))
                      : (_openBlock(), _createBlock(IconCalendarBlankOutline, { key: 1 }))
                  ]),
                  description: _withCtx(() => [
                    _createElementVNode("p", null, _toDisplayString(loading.value ? _unref(t)('spreed', 'Loading …') : _unref(t)('spreed', 'No upcoming meetings')), 1)
                  ]),
                  _: 1
                })),
            (canScheduleMeeting.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_unref(NcButton), {
                    wide: "",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (isFormOpen.value = true))
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(IconPlus, { size: 20 })
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Schedule a meeting')), 1)
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["container", "no-focus-trap"]),
        (canScheduleMeeting.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(_unref(NcDialog), {
                id: "calendar-meeting",
                open: isFormOpen.value,
                "onUpdate:open": _cache[8] || (_cache[8] = ($event: any) => ((isFormOpen).value = $event)),
                class: "calendar-meeting",
                name: _unref(t)('spreed', 'Schedule a meeting'),
                size: "normal",
                "close-on-click-outside": "",
                container: __props.container
              }, {
                actions: _withCtx(() => [
                  (invalidHint.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(invalidHint.value), 1))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(NcButton), {
                    variant: "primary",
                    disabled: !selectedCalendar.value || submitting.value || !!invalid.value,
                    onClick: submitNewMeeting
                  }, {
                    icon: _withCtx(() => [
                      (submitting.value)
                        ? (_openBlock(), _createBlock(_unref(NcLoadingIcon), {
                            key: 0,
                            size: 20
                          }))
                        : (_openBlock(), _createBlock(IconCheck, {
                            key: 1,
                            size: 20
                          }))
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Save')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                default: _withCtx(() => [
                  _createVNode(_unref(NcTextField), {
                    modelValue: newMeetingTitle.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newMeetingTitle).value = $event)),
                    label: _unref(t)('spreed', 'Meeting title'),
                    "label-visible": ""
                  }, null, 8, ["modelValue", "label"]),
                  _createVNode(_unref(NcTextArea), {
                    modelValue: newMeetingDescription.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newMeetingDescription).value = $event)),
                    label: _unref(t)('spreed', 'Description'),
                    resize: "vertical",
                    "label-visible": ""
                  }, null, 8, ["modelValue", "label"]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_unref(NcDateTimePickerNative), {
                      id: "schedule_meeting_input",
                      modelValue: selectedDateTimeStart.value,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedDateTimeStart).value = $event)),
                      class: _normalizeClass({ 'invalid-time': invalid.value === 'start' }),
                      min: new Date(),
                      step: 300,
                      label: _unref(t)('spreed', 'From'),
                      type: "datetime-local"
                    }, null, 8, ["modelValue", "class", "min", "label"]),
                    _createVNode(_unref(NcDateTimePickerNative), {
                      id: "schedule_meeting_input",
                      modelValue: selectedDateTimeEnd.value,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedDateTimeEnd).value = $event)),
                      class: _normalizeClass({ 'invalid-time': invalid.value === 'end' }),
                      min: new Date(),
                      step: 300,
                      label: _unref(t)('spreed', 'To'),
                      type: "datetime-local"
                    }, null, 8, ["modelValue", "class", "min", "label"])
                  ]),
                  _createVNode(_unref(NcSelect), {
                    id: "schedule_meeting_select",
                    modelValue: selectedCalendar.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedCalendar).value = $event)),
                    options: calendarOptions.value,
                    "input-label": _unref(t)('spreed', 'Calendar')
                  }, {
                    "selected-option": _withCtx((option) => [
                      _createElementVNode("span", {
                        class: "calendar-badge",
                        style: _normalizeStyle({ backgroundColor: option.color })
                      }, null, 4),
                      _createTextVNode(" " + _toDisplayString(option.label), 1)
                    ]),
                    option: _withCtx((option) => [
                      _createElementVNode("span", {
                        class: "calendar-badge",
                        style: _normalizeStyle({ backgroundColor: option.color })
                      }, null, 4),
                      _createTextVNode(" " + _toDisplayString(option.label), 1)
                    ]),
                    _: 1
                  }, 8, ["modelValue", "options", "input-label"]),
                  (!isOneToOneConversation.value)
                    ? (_openBlock(), _createElementBlock("h5", _hoisted_6, _toDisplayString(_unref(t)('spreed', 'Attendees')), 1))
                    : _createCommentVNode("", true),
                  (!participantsInitialised.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        _createVNode(_unref(NcLoadingIcon)),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Loading …')), 1)
                      ]))
                    : (participants.value.length === 0)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(_unref(t)('spreed', 'No other participants to send invitations to.')), 1))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                          _createVNode(_unref(NcCheckboxRadioSwitch), {
                            modelValue: selectAll.value,
                            "onUpdate:modelValue": [
                              _cache[6] || (_cache[6] = ($event: any) => ((selectAll).value = $event)),
                              toggleAll
                            ]
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(inviteLabel.value), 1)
                            ]),
                            _: 1
                          }, 8, ["modelValue"]),
                          (!isOneToOneConversation.value && !selectAll.value)
                            ? (_openBlock(), _createBlock(_unref(NcButton), {
                                key: 0,
                                variant: "tertiary",
                                onClick: _cache[7] || (_cache[7] = ($event: any) => (isSelectorOpen.value = true))
                              }, {
                                icon: _withCtx(() => [
                                  _createVNode(IconAccountPlusOutline, { size: 20 })
                                ]),
                                default: _withCtx(() => [
                                  _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Add attendees')), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          _createElementVNode("p", null, _toDisplayString(attendeeHint.value), 1)
                        ], 64))
                ]),
                _: 1
              }, 8, ["open", "name", "container"]),
              (isSelectorOpen.value)
                ? (_openBlock(), _createBlock(_unref(NcDialog), {
                    key: 0,
                    open: isSelectorOpen.value,
                    "onUpdate:open": _cache[13] || (_cache[13] = ($event: any) => ((isSelectorOpen).value = $event)),
                    name: _unref(t)('spreed', 'Add attendees'),
                    class: "calendar-meeting",
                    "close-on-click-outside": "",
                    container: "#calendar-meeting"
                  }, {
                    actions: _withCtx(() => [
                      _createVNode(_unref(NcButton), {
                        variant: "primary",
                        onClick: _cache[12] || (_cache[12] = ($event: any) => (isSelectorOpen.value = false))
                      }, {
                        icon: _withCtx(() => [
                          _createVNode(IconCheck, { size: 20 })
                        ]),
                        default: _withCtx(() => [
                          _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Done')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    default: _withCtx(() => [
                      _createVNode(SearchBox, {
                        value: searchText.value,
                        "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((searchText).value = $event)),
                        class: "calendar-meeting__searchbox",
                        "is-focused": "",
                        "placeholder-text": _unref(t)('spreed', 'Search participants'),
                        onAbortSearch: _cache[10] || (_cache[10] = ($event: any) => (searchText.value = ''))
                      }, null, 8, ["value", "placeholder-text"]),
                      (selectedAttendeeIds.value.length)
                        ? (_openBlock(), _createBlock(TransitionWrapper, {
                            key: 0,
                            class: "calendar-meeting__attendees-selected",
                            name: "zoom",
                            tag: "div",
                            group: ""
                          }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedParticipants.value, (participant) => {
                                return (_openBlock(), _createBlock(ContactSelectionBubble, {
                                  key: participant.actorType + participant.actorId,
                                  participant: participant,
                                  onUpdate: removeSelectedParticipant
                                }, null, 8, ["participant"]))
                              }), 128))
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      (participantsInitialised.value && filteredParticipants.value.length)
                        ? (_openBlock(), _createElementBlock("ul", _hoisted_10, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredParticipants.value, (participant) => {
                              return (_openBlock(), _createBlock(SelectableParticipant, {
                                key: participant.attendeeId,
                                checked: selectedAttendeeIds.value,
                                "onUpdate:checked": [
                                  _cache[11] || (_cache[11] = ($event: any) => ((selectedAttendeeIds).value = $event)),
                                  checkSelection
                                ],
                                participant: participant
                              }, null, 8, ["checked", "participant"]))
                            }), 128))
                          ]))
                        : (_openBlock(), _createBlock(_unref(NcEmptyContent), {
                            key: 2,
                            class: "calendar-meeting__empty-content",
                            name: !participantsInitialised.value ? _unref(t)('spreed', 'Loading …') : _unref(t)('spreed', 'No results')
                          }, {
                            icon: _withCtx(() => [
                              (!participantsInitialised.value)
                                ? (_openBlock(), _createBlock(_unref(NcLoadingIcon), { key: 0 }))
                                : (_openBlock(), _createBlock(IconAccountSearchOutline, { key: 1 }))
                            ]),
                            _: 1
                          }, 8, ["name"]))
                    ]),
                    _: 1
                  }, 8, ["open", "name"]))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})